// Core application types - consolidated from types.ts and types/index.ts

export type Sport = 'Football' | 'FlagFootball' | 'Basketball' | 'Soccer' | 'Baseball' | 'Softball' | 'Volleyball' | 'Hockey' | 'Lacrosse' | 'Tennis';

export type AgeLevel = '8U' | '9U' | '10U' | '11U' | '12U' | '13U' | '14U' | 'HS' | 'College' | 'Adult';

/**
 * Canonical, ordered list of AgeLevel values for dropdowns — single source of
 * truth so TeamSetup.tsx and TeamInformationSection.tsx (previously two
 * separately hardcoded, already-out-of-sync arrays missing 'HS') can't drift
 * again when a new level is added.
 */
export const AGE_LEVELS: AgeLevel[] = ['8U', '9U', '10U', '11U', '12U', '13U', '14U', 'HS', 'College', 'Adult'];

/** Display label for an AgeLevel — only 'Adult' needs more than its raw value. */
export function getAgeLevelLabel(level: AgeLevel): string {
  return level === 'Adult' ? 'Adult (18+)' : level;
}

export type ExperienceLevel = 'Beginner' | 'Intermediate' | 'Advanced';

// Sport formats configuration
export const SportFormats: Record<Sport, string[]> = {
  Football: ['5v5', '6v6', '7v7', '8v8', '9v9', '11v11'],
  FlagFootball: ['4v4', '5v5', '6v6', '7v7', '8v8'],
  Basketball: ['1v1', '2v2', '3v3', '4v4', '5v5'],
  Baseball: ['9v9'],
  Softball: ['10v10', '9v9'],
  Soccer: ['3v3', '4v4', '5v5', '6v6', '7v7', '9v9', '11v11'],
  Volleyball: ['2v2', '3v3', '4v4', '6v6'],
  Hockey: ['3v3', '4v4', '5v5', '6v6'],
  Lacrosse: ['3v3', '4v4', '6v6', '7v7', '10v10', '12v12'],
  Tennis: ['Singles', 'Doubles', 'Mixed Doubles'],
};

// Sport-specific scheme types
export type FootballOffensiveScheme = 'Spread' | 'Pro Style' | 'Wing-T' | 'Option' | 'Single Wing' | 'Air Raid' | 'West Coast' | 'Run and Shoot';
export type FootballDefensiveScheme = '4-3' | '3-4' | '5-2' | 'Cover 2' | 'Cover 3' | '4-2-5' | '3-3-5' | 'Tampa 2' | '4-6 Bear';

export type BasketballOffensiveScheme = 'Motion' | 'Princeton' | 'Triangle' | 'Pick and Roll' | 'Fast Break' | 'Flex' | 'Zone';
export type BasketballDefensiveScheme = 'Man-to-Man' | '2-3 Zone' | '3-2 Zone' | '1-3-1 Zone' | 'Box and One' | 'Full Court Press' | 'Half Court Press';

export type SoccerFormation = '4-4-2' | '4-3-3' | '3-5-2' | '5-3-2' | '4-2-3-1' | '3-4-3' | '4-5-1' | '4-1-4-1';
export type SoccerStyle = 'Possession' | 'Counter-Attack' | 'High Press' | 'Tiki-Taka' | 'Direct' | 'Total Football' | 'Park the Bus';

export type BaseballFocus = 'Small Ball' | 'Power Hitting' | 'Defensive' | 'Balanced' | 'Pitching Focus' | 'Speed Focus';

export type HockeySystem = 'Dump and Chase' | 'Neutral Zone Trap' | 'Left Wing Lock' | '1-2-2 Forecheck' | '2-1-2 Forecheck';

export type VolleyballOffense = '5-1' | '6-2' | '4-2' | 'International' | 'West Coast';
export type VolleyballDefense = 'Rotation' | 'Perimeter' | 'Man-Up' | '6-Up' | 'Base';

export type LacrosseOffense = 'Motion' | '1-4-1' | '2-3-1' | '3-3' | 'Wheel';
export type LacrosseDefense = 'Man-to-Man' | 'Zone' | 'Backer' | 'Slide Package' | 'Crease Defense';

export type TennisStyle = 'Baseline' | 'Serve-and-Volley' | 'All-Court' | 'Counterpuncher' | 'Aggressive Baseline';

// Backward compatibility aliases
export type OffensiveScheme = FootballOffensiveScheme;
export type DefensiveScheme = FootballDefensiveScheme;

// Play participation tracking for MPR (Minimum Play Requirements)
export interface PlayParticipation {
  hasMPR: boolean;
  minimumRequiredPlays: number;
  playsLogged: number;
  lastUpdatedDrive?: number;
  rotationGroup?: string;
  priorityLevel: 1 | 2 | 3; // 1 = starter (non-MPR), 2 = MPR player, 3 = legacy backup (unused)
  recommendedInsertionDrives?: number[];
  forceInsert: boolean;
  notes?: string;
  driveHistory?: number[];
  eligiblePackages?: string[];
  isAvailable: boolean; // false if injured or missing game
}

// Core entity interfaces
export interface Player {
  id: string;
  first_name: string;
  last_name: string;
  number: number;
  position: string;
  height: string;
  weight: number;
  speed: number;
  strength: number;
  experience: ExperienceLevel;
  encrypted_phone?: string;
  encrypted_email?: string;
  phone_last_updated?: string;
  email_last_updated?: string;
  team_id?: string;
  playParticipation?: PlayParticipation;
  injury_status?: string; // Injury designation: 'H', 'Q', 'D', 'O'
  // Baseball/Softball statistics
  batting_hand?: 'L' | 'R' | 'S'; // Left, Right, or Switch
  batting_average?: number; // 0.000 to 1.000
  on_base_percentage?: number; // 0.000 to 1.000
  /** True if player is a sub (fill-in); subs appear in their own roster section and get lower batting/playing-time priority */
  is_sub?: boolean;
  /** From player card: position group keys (e.g. P, 1B, CF). Empty = eligible for any spot in randomization. */
  eligible_positions?: string[];
  /** From player card: Baseball/Softball role types (battery, infield, outfield) — drives IF/OF focus in randomization. */
  role_types?: string[];
}

export interface Team {
  id: string;
  name: string;
  sport: Sport;
  organization_id?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  ageLevel: AgeLevel;
  experienceLevel: ExperienceLevel;
  rosterSize: number;
  format?: string;
  
  // Generic scheme properties used by all sports
  offensiveScheme?: string;
  defensiveScheme?: string;
  
  // Default formations per tab (can include custom formations)
  defaultOffenseFormation?: string;
  defaultDefenseFormation?: string;
  defaultSpecialTeamsFormation?: string;
  defaultLineupFormation?: string;
  defaultFieldingFormation?: string;
  
  // Baseball/Softball specific settings
  innings?: number; // Number of innings played (varies by age group and league)
  
  // Practice defaults
  defaultPracticeStartTime?: string;
  defaultPracticeEndTime?: string;
  defaultPracticeLocation?: string;
  defaultDrillStepsPerDrill?: number;
  aiUsePositionGroups?: boolean;
  timeFormat?: '12h' | '24h';
  
  // Display preferences
  playerSortBy?: 'firstName' | 'lastName' | 'number';
  
  // Feature toggles
  enableRandomization?: boolean; // Enable position randomization feature

  /** True for the single-player team silently created for the "individual" (parent/player) persona. Hides team-management chrome. */
  isPersonal?: boolean;

  // Timestamps
  created_at?: string;
  updated_at?: string;

  // Related entities
  players?: Player[];
}

export interface Coach {
  id: string;
  name: string;
  team_id: string;
  email?: string;
  phone?: string;
  role?: string;
  avatar_url?: string;
}

// Game Plan types
export interface GamePlan {
  id: string;
  team_id: string;
  title?: string;
  game_date: string;
  home_away?: 'home' | 'away';
  
  // Basic game info
  opponent_name?: string;
  location?: string;
  game_time?: string;
  key_matchups?: string;
  special_considerations?: string;
  
  // AI-generated strategies
  offensive_strategy?: any;
  defensive_strategy?: any;
  special_teams_strategy?: string;
  
  // Substitution plans
  mpr_substitution_plan?: string;
  rotation_packages?: SubstitutionPackage[];
  
  // Generated plays/formations
  recommended_plays?: GamePlanPlay[];
  
  // Additional analysis fields
  sport?: string;
  status?: string;
  draft_data?: any;
  
  created_at?: string;
  updated_at?: string;
}

export interface SubstitutionPackage {
  id: string;
  name: string;
  trigger: string; // e.g., "Start of Q2", "Drive 3", "Red Zone"
  players_in: string[]; // Player IDs
  players_out: string[]; // Player IDs
  formation?: string;
  play_types?: string[];
}

export interface GamePlanPlay {
  id: string;
  name: string;
  formation: string;
  play_type: string; // e.g., "run", "pass", "special"
  situation: string; // e.g., "1st down", "red zone", "short yardage"
  personnel_package: string;
  description: string;
  coaching_points?: string[];
  counters_opponent_tendency?: string;
}

// Practice settings interface
export interface PracticeSettings {
  default_duration: number;
  default_location: string;
  default_start_time: string;
  default_end_time: string;
  position_groups: string[];
  focus_areas: string[];
  themes: string[];
} 